import { FC, ReactNode } from 'react'

import styled, { css } from 'styled-components'

import CheckMarkIcon from 'src/Assets/Svg/pages-icons/CheckMark.svg?react'
import CrossIcon from 'src/Assets/Svg/common/fullCross.svg?react'

export interface LabelForGenreOrLanguageProps {
  title: ReactNode
  onClick: () => void
  count?: number
  selected?: boolean
  disabled?: boolean
  hasCross?: boolean
}

const Container = styled.button<{
  $selected?: boolean
  $hasCross?: boolean
  disabled?: boolean
}>`
  display: flex;
  align-items: center;
  padding: 8px 12px 9px 12px;
  gap: 8px;
  width: fit-content;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  border: 1px solid;
  transition: all 0.2s ease;

  ${({ theme, $selected, $hasCross, disabled }) => {
    if (disabled) {
      return css`
        background-color: ${theme.button.secondaryBg};
        border-color: ${theme.button.secondaryBg};
        color: ${theme.button.primaryBg};
        svg {
          path {
            fill: ${theme.button.primaryBg} !important;
          }
        }
      `
    }
    if ($selected || $hasCross) {
      return css`
        background-color: ${theme.button.fifthBg};
        border-color: ${theme.button.sixthBorder};
        color: ${theme.button.sixthBorder};

        &:hover {
          background-color: ${theme.button.hoverBg};
          border-color: ${theme.button.thirdText};
          color: ${theme.button.thirdText};
          svg {
            path {
              fill: ${theme.button.thirdText} !important;
            }
          }
        }

        &:focus-visible {
          background-color: ${theme.button.hoverBg};
          border-color: ${theme.button.thirdText};
          color: ${theme.button.thirdText};
          outline: none;
          svg {
            path {
              fill: ${theme.button.thirdText} !important;
            }
          }
        }

        &:active {
          background-color: ${theme.button.secondaryBg};
          border-color: ${theme.common.primaryPlaceholder};
          color: ${theme.common.primaryPlaceholder};
          svg {
            path {
              fill: ${theme.common.primaryPlaceholder} !important;
            }
          }
        }
      `
    }
    return css`
      background-color: ${theme.button.fifthBg};
      border-color: ${theme.button.fifthBg};
      color: ${theme.button.fourthBg};

      &:hover {
        background-color: ${theme.button.hoverBg};
        border-color: ${theme.button.hoverBg};
        color: ${theme.button.thirdText};
      }

      &:focus-visible {
        background-color: ${theme.button.hoverBg};
        border-color: ${theme.button.thirdText};
        color: ${theme.button.thirdText};
        outline: none;
      }

      &:active {
        background-color: ${theme.button.secondaryBg};
        border-color: ${theme.common.primaryPlaceholder};
        color: ${theme.common.primaryPlaceholder};
      }
    `
  }}
`

const Text = styled.span`
  display: flex;
  text-align: center;
  gap: 4px;
`

export const LabelForGenreOrLanguage: FC<LabelForGenreOrLanguageProps> = ({
  title,
  onClick,
  count,
  selected,
  disabled,
  hasCross,
}) => (
  <Container
    $selected={selected}
    $hasCross={hasCross}
    disabled={disabled}
    onClick={onClick}
    translate='no'
  >
    <Text>
      {title} {count ? ` (${count})` : ''}
    </Text>
    {hasCross && <CrossIcon role='cross-icon' />}
    {!hasCross && selected && <CheckMarkIcon role='check-icon' />}
  </Container>
)
